/*
 * Cart Upsell Guard
 * -----------------
 * Prevents the Selleasy upsell product from ever surviving alone in the cart.
 *
 * Rule: when the LAST main product is removed (no line item without the
 * `_lb-product` property remains) while a Selleasy upsell line (which carries
 * `properties._lb-product`) is still present, the upsell line(s) are removed
 * automatically and the cart UI is refreshed.
 *
 * Hook: reacts to Dawn's `PUB_SUB_EVENTS.cartUpdate`, which the theme publishes
 * after every line-item change/removal (see assets/cart.js updateQuantity()).
 *
 * Performance: the removal is done in a SINGLE request. We read the post-change
 * cart from the event payload (no extra /cart.js fetch), and ask
 * /cart/update.js to return the re-rendered sections so we update the DOM
 * directly (no second "re-publish + re-fetch section" round trip). This is what
 * keeps the lone upsell from visibly lingering in the drawer.
 *
 * Defensive: fails silently if expected globals / properties are missing.
 */
(function cartUpsellGuard() {
  'use strict';

  // Required Dawn globals (constants.js + pubsub.js load before this file).
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  var UPSELL_PROP = '_lb-product';
  var inFlight = false; // re-entrancy guard against overlapping / repeated fires

  // Resolve cart endpoints, honoring localized roots (e.g. /en-us/).
  function cartEndpoint(file) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + file;
  }

  // True when the line item carries the Selleasy upsell property.
  function isUpsell(item) {
    var props = item && item.properties;
    if (!props || typeof props !== 'object') return false;
    if (!Object.prototype.hasOwnProperty.call(props, UPSELL_PROP)) return false;
    var val = props[UPSELL_PROP];
    return val !== null && val !== undefined && val !== '';
  }

  // The cart sections this page currently shows, mirroring Dawn's own
  // getSectionsToRender() so we replace exactly what Dawn would (and nothing
  // more — e.g. only `.drawer__inner`, never the whole #CartDrawer, so the
  // overlay's click-to-close handler is preserved).
  function sectionTargets() {
    var targets = [];
    if (document.getElementById('CartDrawer')) {
      targets.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      targets.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }
    var mainItems = document.getElementById('main-cart-items');
    if (mainItems && mainItems.dataset.id) {
      targets.push({ id: 'main-cart-items', section: mainItems.dataset.id, selector: '.js-contents' });
    }
    var mainFooter = document.getElementById('main-cart-footer');
    if (mainFooter && mainFooter.dataset.id) {
      targets.push({ id: 'main-cart-footer', section: mainFooter.dataset.id, selector: '.js-contents' });
    }
    return targets;
  }

  function innerHTMLFromSection(html, selector) {
    try {
      var el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return el ? el.innerHTML : null;
    } catch (e) {
      return null;
    }
  }

  // Apply the freshly rendered section HTML returned by /cart/update.js.
  function renderSections(targets, cart) {
    var sections = cart && cart.sections;
    if (sections) {
      targets.forEach(function (t) {
        var container = document.getElementById(t.id);
        if (!container || !sections[t.section]) return;
        var dest = container.querySelector(t.selector) || container;
        var markup = innerHTMLFromSection(sections[t.section], t.selector);
        if (markup !== null) dest.innerHTML = markup;
      });
    }

    // Wrappers that live outside the replaced sections need their empty-state
    // class toggled manually, exactly as Dawn's updateQuantity() does.
    var isEmpty = cart && cart.item_count === 0;
    var cartDrawerWrapper = document.querySelector('cart-drawer');
    if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', isEmpty);
    var cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
  }

  // Remove every upsell line in ONE atomic /cart/update.js call (keyed by line
  // item key, so positional re-indexing is never an issue), requesting the
  // re-rendered sections back so we can refresh the DOM without another fetch.
  function removeUpsellItems(upsellItems) {
    var updates = {};
    upsellItems.forEach(function (item) {
      if (item && item.key) updates[item.key] = 0;
    });
    if (Object.keys(updates).length === 0) return Promise.resolve();

    var targets = sectionTargets();
    var sectionNames = targets.map(function (t) { return t.section; });

    return fetch(cartEndpoint('cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        updates: updates,
        sections: sectionNames.join(','),
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { if (cart) renderSections(targets, cart); });
  }

  // Decide whether the upsell is now orphaned and, if so, remove it.
  function guard(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    var upsellItems = cart.items.filter(isUpsell);
    var mainCount = cart.items.length - upsellItems.length;

    // Only act when the LAST main product is gone but an upsell remains.
    if (mainCount === 0 && upsellItems.length > 0) {
      inFlight = true;
      removeUpsellItems(upsellItems)
        .catch(function () { /* fail silently */ })
        .then(function () { inFlight = false; });
    }
  }

  // Entry point. Uses the event's cart payload when available (the common,
  // fast path), otherwise reads /cart.js (page-load defensive check).
  function evaluate(event) {
    if (inFlight) return; // ignore overlapping triggers

    if (event && event.cartData && Array.isArray(event.cartData.items)) {
      guard(event.cartData);
      return;
    }

    inFlight = true;
    fetch(cartEndpoint('cart.js'), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        inFlight = false;
        guard(cart);
      })
      .catch(function () { inFlight = false; });
  }

  // Primary hook: every cart change the theme publishes.
  subscribe(PUB_SUB_EVENTS.cartUpdate, evaluate);

  // Defensive: catch a cart that already contains only the upsell on page load.
  evaluate();
})();
